import React from "react";
import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import { LocationOn, LocalOffer, Schedule, Psychology } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DetailSection from "./DetailSection";
import { tokens } from "../../store/tokens";

const currencySymbols = {
  1: "৳", // Bangladeshi Taka
  2: "$", // USD
  3: "₹", // Indian Rupee
  4: "€", // Euro
};

const HighlightStat = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <Box sx={{ color: tokens.color.teal, mt: "2px", display: "flex" }}>{icon}</Box>
    <Stack spacing={0.25}>
      <Typography
        sx={{
          fontFamily: tokens.font.body,
          fontSize: "0.875rem",
          fontWeight: 600,
          color: tokens.color.ink,
        }}
      >
        {title || "—"}
      </Typography>
      <Typography
        sx={{ fontFamily: tokens.font.body, fontSize: "0.75rem", color: tokens.color.inkMuted }}
      >
        {subtitle}
      </Typography>
    </Stack>
  </Stack>
);

const JobDetailsCard = ({ fetchJobDetails }) => {
  const job = fetchJobDetails?.[0];
  const navigate = useNavigate();

  if (!job) return null;

  const skills = job?.skill ? JSON.parse(job.skill) : [];

  const handleJobClick = (jobItem) => {
    navigate(`/JobDetails/${jobItem.id}/${jobItem.refer}/${jobItem.userId}`);
  };

  return (
    <Box>
      {/* Title & Category */}
      <DetailSection>
        <Typography
          sx={{
            fontFamily: tokens.font.heading,
            fontSize: "1.15rem",
            fontWeight: 700,
            color: tokens.color.ink,
          }}
        >
          {job.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.font.body,
            fontSize: "0.8rem",
            fontWeight: 600,
            color: tokens.color.teal,
          }}
        >
          {job?.category?.[0]?.title}
        </Typography>
        <Typography
          sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", color: tokens.color.inkMuted }}
        >
          Posted at {job.post_time}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <LocationOn sx={{ fontSize: "0.9rem", color: tokens.color.teal }} />
          <Typography
            sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", color: tokens.color.inkMuted }}
          >
            {job?.poster_info?.Location}
          </Typography>
        </Stack>
      </DetailSection>

      {/* Job Description */}
      <DetailSection>
        <Typography
          sx={{
            fontFamily: tokens.font.body,
            fontSize: "0.875rem",
            color: tokens.color.ink,
            lineHeight: 1.7,
          }}
        >
          {job.details}
        </Typography>
      </DetailSection>

      {/* Budget, Schedule, Experience */}
      <DetailSection>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <HighlightStat
              icon={<LocalOffer sx={{ fontSize: "1.1rem" }} />}
              title={`${currencySymbols[job.currency] || "$"}${job.budget}`}
              subtitle={job.budget_type}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <HighlightStat
              icon={<Schedule sx={{ fontSize: "1.1rem" }} />}
              title={job?.jobSchedule?.[0]?.title}
              subtitle="Project length"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <HighlightStat
              icon={<Psychology sx={{ fontSize: "1.1rem" }} />}
              title={job?.expertLevel?.[0]?.title}
              subtitle={job?.Freelancer?.[0]?.title}
            />
          </Grid>
        </Grid>
      </DetailSection>

      {/* Project Type */}
      <DetailSection>
        <Typography sx={{ fontFamily: tokens.font.body, fontSize: "0.85rem", color: tokens.color.ink }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            Project type:{" "}
          </Box>
          {job?.job_type?.[0]?.title}
        </Typography>
      </DetailSection>

      {/* Skills & Working Limit */}
      <DetailSection>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Typography
                sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", fontWeight: 700, color: tokens.color.ink }}
              >
                Skills and expertise
              </Typography>
              <Stack direction="row" flexWrap="wrap" sx={{ rowGap: 1, columnGap: 1 }}>
                {skills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    size="small"
                    sx={{
                      bgcolor: tokens.color.tealLight,
                      color: tokens.color.tealDark,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "capitalize",
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
              <Typography
                sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", fontWeight: 700, color: tokens.color.ink }}
              >
                Working limit
              </Typography>
              <Typography
                sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", color: tokens.color.inkMuted }}
              >
                {job?.jobHour?.[0]?.title}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </DetailSection>

      {/* Job Activity */}
      <DetailSection title="Activity on this job">
        <Stack spacing={0.5}>
          {[
            ["Proposals", job.total_proposal],
            ["Interviewing", job.total_interview],
            ["Invites sent", job.total_invite],
          ].map(([label, value]) => (
            <Stack
              key={label}
              direction="row"
              justifyContent="space-between"
              sx={{ maxWidth: 260 }}
            >
              <Typography
                sx={{ fontFamily: tokens.font.body, fontSize: "0.8125rem", color: tokens.color.inkMuted }}
              >
                {label}
              </Typography>
              <Typography
                sx={{ fontFamily: tokens.font.body, fontSize: "0.8125rem", fontWeight: 700, color: tokens.color.ink }}
              >
                {value ?? 0}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </DetailSection>

      {/* Other jobs from this poster */}
      <DetailSection title="Other jobs from this poster" noBorder>
        {job?.poster_active_job?.length > 0 ? (
          <Stack spacing={0.5}>
            {job.poster_active_job.map((jobItem) => (
              <Box
                key={jobItem.id}
                onClick={() => handleJobClick(jobItem)}
                sx={{
                  padding: "10px 12px",
                  borderRadius: `${tokens.radius.sm}px`,
                  cursor: "pointer",
                  transition: "background-color 0.18s ease",
                  "&:hover": { bgcolor: tokens.color.tealLight },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: tokens.font.body,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: tokens.color.ink,
                    lineHeight: 1.4,
                  }}
                >
                  {jobItem.title}
                </Typography>
                <Typography
                  sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", color: tokens.color.inkMuted }}
                >
                  Budget: {currencySymbols[jobItem.currency] || "$"}
                  {jobItem.budget}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography
            sx={{
              fontFamily: tokens.font.body,
              fontSize: "0.85rem",
              color: tokens.color.inkFaint,
              textAlign: "center",
            }}
          >
            No active jobs found.
          </Typography>
        )}
      </DetailSection>
    </Box>
  );
};

export default JobDetailsCard;